<template>
  <div class="flex">
    <div
      class="fixed top-30 w-88 flex-shrink-0 border-r border-slate-400 bg-pure-white"
      :style="{ zIndex: 51 }"
    >
      <div class="p-6 border-b border-slate-400 flex flex-col gap-3">
        <div>
          <div class="mb-1"><BlTextBody2 text="Availabilities" /></div>
          <BlTextTitle3 text="Rooms" />
        </div>
        <div
          class="flex p-1 bg-slate-400"
          :style="{ borderRadius: '8px', height: '2.375rem' }"
        >
          <div
            v-for="opt in typeOptions"
            :key="opt.value"
            class="rounded flex items-center justify-center h-full cursor-pointer w-full transition-all duration-300"
            :class="typeInput === opt.value ? 'bg-pure-white' : 'bg-slate-400'"
            @click="onChangeType(opt.value)"
          >
            <BlTextSubtitle3 :text="opt.label" />
          </div>
        </div>
        <BlInputButton
          type="outlined"
          icon="plus"
          label="Room"
          @clicked="newOpened = true"
        />
      </div>
      <div
        v-if="pageLoading"
        class="w-full grid place-items-center"
        :style="{ height: 'calc(100vh - 21.75rem)' }"
      ></div>
      <div
        v-else
        class="overflow-y-auto"
        :style="{ height: 'calc(100vh - 21.75rem)' }"
      >
        <div v-if="typeInput === 'daily'" id="therapist-section">
          <div class="border-b border-slate-400 flex justify-center relative">
            <div
              class="absolute z-10 flex items-center gap-1 bg-pure-white"
              :style="{ right: '10px', top: '8px', height: '32px' }"
            >
              <BlInputButton
                type="outlined"
                dense
                label="Today"
                class="px-2"
                @clicked="onDayClick({ date: new Date() })"
              />
              <BlInputButton
                type="outlined"
                dense
                icon="chevron-left"
                class="border-none"
                @clicked="onMoveCalendar(-1)"
              />
              <BlInputButton
                type="outlined"
                dense
                icon="chevron-right"
                class="border-none"
                @clicked="onMoveCalendar(1)"
              />
            </div>
            <v-calendar
              ref="calendar"
              is-expanded
              hide-header
              title-position="left"
              :attributes="attrs"
              class="border-none custom-calendar"
              @dayclick="onDayClick"
            />
          </div>
          <div class="py-5 px-6">
            <div class="flex items-center justify-between mb-2">
              <BlTextSubtitle3
                text="Filter by Therapist"
                class="text-slate-600"
              />
            </div>
            <div class="flex items-center gap-2">
              <BlIcon icon="user" color="slate-6" class="h-6 w-6" />
              <BlSingleFilter
                class="w-68"
                label="Therapist"
                placeholder="By therapist"
                v-model="filterTherapist"
                :options="[{ value: 'open_room', label: 'Open room' }]"
                search
                searchPlaceholder="Search by name"
                :fetchProperty="{
                  url: `/api/v1/users?sort=name_asc&status=active&query=`,
                  dataKey: 'users',
                  countKey: 'total_users',
                  optionKeys: { value: 'id', label: 'name' }
                }"
                @change="onFilterTherapist"
              />
            </div>
          </div>
        </div>
        <div v-if="typeInput === 'weekly'" id="room-section">
          <div v-if="enableBranch">
            <RoomGroupBranch
              v-for="branch in branches"
              :key="branch.id"
              :title="branch.name"
              :rooms="rooms.filter(i => i.branch_id === branch.id)"
              :selectedRoomId="selectedRoomId"
              @select-room="onSelectRoom($event)"
            />
            <RoomGroupBranch
              title="No branch"
              :rooms="rooms.filter(i => !i.branch_id)"
              :selectedRoomId="selectedRoomId"
              @select-room="onSelectRoom($event)"
            />
          </div>
          <div v-else>
            <RoomGroupBranch
              title="All rooms"
              :rooms="rooms"
              :selectedRoomId="selectedRoomId"
              @select-room="onSelectRoom($event)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="pl-88">
      <div
        class="h-12 px-6 sticky top-30 z-30 bg-slate-300 border-b border-slate-400 flex items-center justify-between gap-4"
        :style="{ minWidth: 'calc(100vw - 22rem - 0.5rem)' }"
      >
        <div class="sticky top-30 left-94 flex items-center gap-2">
          <BlSelectBox
            v-if="enableBranch"
            class="max-w-48"
            type="branch"
            v-model="selectedBranchId"
            :options="branchOptions"
            search
            @change="selectedBranchId = $event"
          />
          <div class="flex-shrink-0">
            <BlTextBody2 :text="masterDate" />
          </div>
        </div>
        <div class="sticky top-30 right-6 flex items-center gap-2">
          <div>
            <BlSelectBox
              v-model="timeslotView"
              :options="timeslotViewOptions"
              dense
              @change="$emit('set-timeslot-view', $event)"
            />
          </div>
          <BlInputButton
            type="outlined"
            dense
            label="Jump to"
            @clicked="jumpToOpened = true"
          />
          <BlInputButton
            type="outlined"
            dense
            label="Today"
            @clicked="onDayClick({ date: new Date() })"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-left"
            @clicked="onNextClick(-1)"
          />
          <BlInputButton
            type="outlined"
            dense
            class="rounded-full"
            icon="chevron-right"
            @clicked="onNextClick(1)"
          />
        </div>
      </div>
      <Timeslots
        :isCompact="timeslotView === 'compact'"
        :selectedTherapist="null"
        :selectedRoom="rooms.find(i => i.id === selectedRoomId) || null"
        :selectedDate="date"
        :headers="timeslotHeaders"
        :loading="previewLoading"
        @after-commit="fetchAvailabilities"
      />
    </div>
    <NewRoomModal v-if="newOpened" @close-modal="newOpened = false" />
    <JumpToDateModal
      v-if="jumpToOpened"
      :currentDate="date"
      :dimension="typeInput"
      @close-modal="jumpToOpened = false"
      @submit="onDayClick({ date: $event })"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import NewRoomModal from "./room/NewRoomModal.vue";
import RoomGroupBranch from "./room/GroupBranch.vue";
import JumpToDateModal from "./timeslot/JumpToDateModal.vue";
import Timeslots from "./timeslot/Timeslots.vue";

import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlIcon from "../BlIcon.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputCheck from "../BlInputCheck.vue";
import BlSelectBox from "../BlSelectBox.vue";
import BlSingleFilter from "../BlSingleFilter.vue";

export default {
  name: "AvailabilitiesRoom",
  components: {
    NewRoomModal,
    RoomGroupBranch,
    JumpToDateModal,
    Timeslots,
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputButton,
    BlInputCheck,
    BlSelectBox,
    BlSingleFilter
  },
  props: {
    timeslotView: { type: String },
    timeslotViewOptions: { type: Array }
    // @set-timeslot-view
  },
  data() {
    return {
      pageLoading: true,
      previewLoading: false,
      date: new Date(),
      typeInput: "daily", // daily | weekly
      typeOptions: [
        { value: "daily", label: "Daily" },
        { value: "weekly", label: "Weekly" }
      ],
      newOpened: false,
      filterTherapist: [],
      selectedRoomId: 0,
      selectedBranchId: 0,
      jumpToOpened: false
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "branches",
      "rooms",
      "totalRooms",
      "availabilities"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    branchOptions() {
      const allBranch = {
        value: 0,
        label: "All branches",
        class: "font-bold text-light-purple -mx-1"
      };
      const arr = this.branches.map(i => ({
        value: i.id,
        label: i.name,
        color: i.color,
        backgroundColor: i.background_color
      }));
      const noBranch = {
        value: "no",
        label: "No branch",
        color: "#667085",
        backgroundColor: "#F9FAFB"
      };
      return [allBranch, ...arr, noBranch];
    },
    getParams() {
      let d = moment(this.date).format("YYYY-MM-DD");
      if (this.typeInput === "weekly") {
        d = moment(this.date)
          .startOf("isoWeek")
          .format("YYYY-MM-DD");
      }
      let params = `?date=${d}&type=${this.typeInput}`;
      if (this.typeInput === "daily") {
        if (this.filterTherapist.length) {
          params += `&user_id=${this.filterTherapist.join(",")}`;
        }
      }
      if (this.typeInput === "weekly") {
        if (this.selectedRoomId) {
          params += `&room_id=${this.selectedRoomId}`;
        }
      }
      return params;
    },
    attrs() {
      const solid = "bg-light-purple border-2 border-light-purple";
      const light = "bg-light-purple-200 border-2 border-light-purple-200";

      const today = {
        key: "today",
        highlight: { class: solid, fillMode: "solid" },
        dates: new Date()
      };
      const selected = {
        key: "selected",
        highlight: { class: light, fillMode: "light" },
        dates: this.date
      };

      const isSameDay =
        moment().format("YYYY-MM-DD") ===
        moment(this.date).format("YYYY-MM-DD");
      if (isSameDay) return [today];

      return [today, selected];
    },
    masterDate() {
      if (this.typeInput === "daily") {
        return moment(this.date).format("dddd, DD MMM YYYY");
      } else {
        const start = moment(this.date)
          .startOf("isoWeek")
          .format("ddd, DD MMM YYYY");
        const end = moment(this.date)
          .endOf("isoWeek")
          .format("ddd, DD MMM YYYY");
        return `${start} — ${end}`;
      }
    },
    timeslotHeaders() {
      if (this.typeInput === "daily") {
        return [...this.rooms]
          .sort((a, b) => {
            if (!this.enableBranch) return true;
            const branchA = a.branch_id ? a.branch : { name: "z" };
            const branchB = b.branch_id ? b.branch : { name: "z" };
            const textA = branchA.name.toUpperCase();
            const textB = branchB.name.toUpperCase();
            return textA < textB ? -1 : textA > textB ? 1 : 0;
          })
          .filter(i => {
            if (!this.enableBranch) return true;
            if (!this.selectedBranchId) return true;
            if (this.selectedBranchId === "no") return !i.branch_id;
            return i.branch_id === this.selectedBranchId;
          })
          .map(i => {
            const branch = i.branch_id ? i.branch : null;
            return { ...i, value: i.id, label: i.name, branch };
          });
      } else {
        const startDate = moment(this.date)
          .startOf("isoWeek")
          .format("YYYY-MM-DD");
        const endDate = moment(this.date)
          .endOf("isoWeek")
          .format("YYYY-MM-DD");

        let current = moment(startDate);

        const dates = [];

        while (current.isSameOrBefore(endDate)) {
          const isSame =
            current.format("YYYY-MM-DD") === moment().format("YYYY-MM-DD");
          dates.push({
            highlight: isSame,
            value: current.format("YYYY-MM-DD"),
            label: current.format("ddd DD")
          });
          current.add(1, "days");
        }

        return dates;
      }
    }
  },
  watch: {
    rooms: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val && val.length) {
          if (!this.selectedRoomId) {
            let id = val[0].id;
            if (id) this.onSelectRoom(id);
          }
        }
      }
    }
  },
  async mounted() {
    const isValidDate = d => {
      return d instanceof Date && !isNaN(d);
    };

    const searchParams = this.$route.query;
    if (searchParams["date"]) {
      const d = new Date(`${searchParams["date"]}T00:00:00`);
      if (isValidDate(d)) this.date = d;
    }
    if (searchParams["type"]) {
      const found = this.typeOptions.find(
        i => i.value === searchParams["type"]
      );
      if (found) this.typeInput = found.value;
    }
    if (searchParams["user_id"]) {
      this.filterTherapist = searchParams["user_id"]
        .split(",")
        .map(i => {
          if (i === "open_room") return i;
          else return Number(i);
        })
        .filter(i => i);
    }
    if (searchParams["room_id"]) {
      const n = Number(searchParams["room_id"]);
      if (Number(n)) this.selectedRoomId = Number(n);
    }

    this.pageLoading = true;
    this.previewLoading = true;

    this.getBranches({ params: "?sort=name_asc" });
    await this.getRooms();
    this.fetchAvailabilities();
  },
  methods: {
    ...mapActions(["getUsers", "getBranches", "getRooms", "getAvailabilities"]),
    fetchAvailabilities() {
      if (this.$route.path !== "/availabilities") return;

      this.previewLoading = true;
      this.getAvailabilities({ params: this.getParams }).then(async () => {
        this.previewLoading = false;
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/availabilities${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onChangeType(type) {
      this.typeInput = type;
      this.fetchAvailabilities();
    },
    async onSyncCalendar(d) {
      const month = moment(d).format("M");
      const year = moment(d).format("YYYY");
      const calendar = this.$refs.calendar;
      if (calendar) await calendar.move({ month, year });
    },
    onDayClick(day) {
      const d = day.date;
      this.date = d;
      this.onSyncCalendar(d);
      this.fetchAvailabilities();
    },
    async onMoveCalendar(month) {
      const calendar = this.$refs.calendar;
      if (calendar) await calendar.move(month);
    },
    onNextClick(num) {
      const change = this.typeInput === "daily" ? 1 : 7;
      const d = new Date(this.date);
      if (num === 1) {
        this.onDayClick({ date: d.setDate(d.getDate() + change) });
      } else {
        this.onDayClick({ date: d.setDate(d.getDate() - change) });
      }
    },
    onlyUnique(value, index, array) {
      return array.map(i => i.value).indexOf(value.value) === index;
    },
    isDiff(arr, arr2) {
      if (arr.length !== arr2.length) {
        return true;
      } else {
        let diff = false;
        arr.forEach((i, idx) => {
          if (i !== arr2[idx]) diff = true;
        });
        return diff;
      }
    },
    onFilterTherapist(val) {
      const isDiff = this.isDiff(this.filterTherapist, val);
      this.filterTherapist = val;
      if (isDiff) {
        this.fetchAvailabilities();
      }
    },
    onSelectTherapistAll(arr) {
      this.selectedTherapistIds = arr;
      if (this.typeInput === "daily") this.fetchAvailabilities();
    },
    onSelectTherapist(id) {
      if (this.selectedTherapistIds.includes(id)) {
        const idx = this.selectedTherapistIds.indexOf(id);
        this.selectedTherapistIds.splice(idx, 1);
      } else this.selectedTherapistIds.push(id);
      this.fetchAvailabilities();
    },
    onSelectRoom(id) {
      this.selectedRoomId = Number(id);
      if (this.typeInput === "weekly") this.fetchAvailabilities();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/availabilities/Room.vue